import { API_CALL, LERE_DISMISS_STICKY_ABANDONED_CART, OFFER_CALENDAR_RECOMMENDATIONS_CLEAR, LERE_REMOVE_HIGH_INTENT, LERE_SAVE_ABANDONED_CART, LERE_SAVE_HOT_LEADS, SET_LERE_VERSION, LERE_DISMISS_ALL_STICKY_ABANDONED_CART, LERE_REMOVE_ABANDONED_CART, LERE_DISMISS_USER_PREFERENCES_NOTIFICATION, LERE_HAS_DISMISSED_USER_PREFERENCES_NOTIFICATION, LERE_SAVE_DEFAULT_RECOMMENDATION } from 'actions/actionConstants'
import {
  FETCH_CALENDAR_OFFER_RECOMMENDATIONS,
  FETCH_LERE_LOCATION_SEEDED_RECOMMENDATIONS,
  FETCH_OFFER_BASED_LERE_RECOMMENDATIONS,
  FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME,
  FETCH_LERE_TOP_DEALS,
  FETCH_LERE_HERO_PLANNER,
  FETCH_LERE_HIGH_INTENT,
  FETCH_LERE_OFFER_STATS,
  FETCH_LERE_PERSONALISED_ALTERNATIVES,
  GET_LOCAL_HIGH_INTENT_OFFERS,
  FETCH_USER_PREFERENCES,
  SUBMIT_USER_PREFERENCES,
  FETCH_LERE_TRENDING_DESTINATIONS,
  FETCH_LERE_PERSONALISED_ENSEMBLE_MODEL,
} from 'actions/apiActionConstants'
import * as RecommendationService from 'api/recommendations'
import { lereLocationSeededCacheKey, lereDlpParameters } from 'lib/customer/recommendationUtils'
import config from 'constants/config'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import { getCurrentUserId } from 'selectors/accountSelectors'
import { timeNowInSecond } from 'lib/datetime/time'
import { getRecommendationUserId } from 'selectors/accountSelectors'
import { getAllAbandonedCarts, getLatestAbandonedCart, getRecentlyViewedOffers } from 'selectors/recomendations/highIntentSelectors'
import { getAllHotLeadOffers } from 'selectors/recomendations/hotLeadSelectors'
import { getOfferStatsKey } from 'selectors/recomendations/offerStatsSelectors'
import { FETCH_LERE_PERSONALISED_ALTERNATIVES_WITH_OFFER_ENQUIRES } from './apiActionConstants'
import { removeHighIntentOffersFromLocal, getHighIntentOffersFromLocal, saveHighIntentOffersToLocal } from 'components/Recommendations/utils/highIntentLocal'
import { ALL_GRAPH_RECOMMENDATION_OFFER_TYPES } from 'constants/recommendations'
import { dismissUserPreferencesNotificationLocally, getHasDismissedUserPreferencesNotificationLocally } from '../components/Recommendations/utils/dismissUserPreferencesNotification'
import { set as localStorageSet, get as localStorageGet } from 'lib/storage/expirableLocalStorage'
import { constructAlternativeOffersKey, getAlternativeOfferEnquiry } from 'selectors/recomendations/personalisedAlternatives'

/**
 *
 * @param offerId offer id
 * @param mergeResults sometimes, you do not want to remove the existing state (that could cause the LERE ui touchpoint to re-render, for example). Then, use mergeResults - it will append new offer ids to the end.
 * @param checkIn Check in date in ISO format
 * @param checkOut Check out date in ISO format
 * @returns
 */
export function fetchOfferBasedRecommendations(offerId: string, mergeResults: boolean = false, allowMultiOffersSameProperty: boolean = false, checkIn?: string, checkOut?: string) {
  return async(dispatch, getState: () => App.State) => {
    const state = getState()
    const cacheKey = offerId

    let checkInParam: string | undefined = undefined
    let checkOutParam: string | undefined = undefined
    if (checkIn && checkOut) {
      checkInParam = checkIn
      checkOutParam = checkOut
    }

    const filterKey = (!!checkInParam && !!checkOutParam) ? [checkIn, checkOut].join(';') : '-'

    const recommendationsState = state.recommendations.recommendations[cacheKey]

    if (['loading', 'done'].includes(recommendationsState?.state)) {
      if (mergeResults) {
        if (recommendationsState?.filtersFetched.includes(filterKey)) {
          return
        }
      } else {
        return
      }
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_BASED_LERE_RECOMMENDATIONS,
      request: async() => {
        const result = await RecommendationService.getRecommendations(offerId, state.geo.currentRegionCode, allowMultiOffersSameProperty, checkInParam, checkOutParam)
        const { recommendations, lereVersion } = result
        dispatch({ type: SET_LERE_VERSION, lereVersion })

        const recommendationOfferIds = recommendations.map((r) => r.offerId)

        return recommendationOfferIds
      },
      cacheKey,
      filterKey,
      mergeResults,
    })
  }
}

let _requestId = 1

export function fetchCalendarRecommendations(offerId: string, filters: {
  checkIn?: string;
  availableMonth: string;
  rooms?: App.Occupants[];
  duration?: number;
}) {
  return async(dispatch, getState: () => App.State) => {
    const state = getState()
    _requestId++

    dispatch({
      type: API_CALL,
      api: FETCH_CALENDAR_OFFER_RECOMMENDATIONS,
      request: async() => {
        const params = {
          checkIn: filters.checkIn,
          availableMonth: filters.availableMonth,
          rooms: filters.rooms,
          duration: filters.duration,
        }

        const { recommendations, lereVersion } = await RecommendationService.getCalendarRecommendations(offerId, state.geo.currentRegionCode, params)
        const recommendationOfferIds = recommendations.map((r) => r.offerId)
        dispatch({ type: SET_LERE_VERSION, lereVersion })
        return {
          offerIds: recommendationOfferIds,
          requestId: _requestId,
        }
      },
      offerId,
      requestId: _requestId,
    })
  }
}

export function clearCalendarOfferRecommendations() {
  return {
    type: OFFER_CALENDAR_RECOMMENDATIONS_CLEAR,
  }
}

interface FetchLocationSeededRecommendationsParams {
  destinationId: string
  polygonMode: RecommendationService.PolygonMode
  nearDestinations: boolean
  useCacheIfExists?: boolean
  checkIn?: string
  checkOut?: string
  duration?: number
  useCentroid?: boolean
  offerTypes?: string[]
}

export function fetchLocationSeededRecommendations({
  destinationId,
  polygonMode,
  nearDestinations,
  useCacheIfExists = false,
  checkIn,
  checkOut,
  duration,
  useCentroid = true,
  offerTypes,
}: FetchLocationSeededRecommendationsParams) {
  return async(dispatch, getState: () => App.State) => {
    const destinationKey = lereLocationSeededCacheKey({
      destinationId,
      polygonMode,
      nearDestinations,
      checkIn,
      checkOut,
      duration,
      offerTypes,
    })

    if (useCacheIfExists) {
      const existingRecommendations = getState().recommendations.locationSeededRecommendations[destinationKey]
      if (existingRecommendations && ['loading', 'done'].includes(existingRecommendations.state)) {
        return
      }
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_LOCATION_SEEDED_RECOMMENDATIONS,
      request: async() => {
        let checkInParam: string | undefined = undefined
        let checkOutParam: string | undefined = undefined
        let durationParam: number | undefined = undefined
        if (checkIn && checkOut) {
          checkInParam = checkIn
          checkOutParam = checkOut
        }
        if (checkIn && duration) {
          checkInParam = checkIn
          durationParam = duration
        }
        const { recommendations, lereVersion } = await RecommendationService.getLocationSeededRecommendations({
          destinationId,
          polygonMode,
          nearDestination: nearDestinations,
          checkInDate: checkInParam,
          checkOutDate: checkOutParam,
          duration: durationParam,
          useCentroid,
          offerTypes,
        })
        dispatch({ type: SET_LERE_VERSION, lereVersion })

        const recommendationOfferIds = recommendations.map((r) => r.offerId)
        return recommendationOfferIds
      },
      destinationKey,
    })
  }
}

// #region DLP - Destination Landing Page

export function fetchDlpMainRecommendations(destinationId: string) {
  return fetchLocationSeededRecommendations({
    destinationId,
    polygonMode: lereDlpParameters.mainList.polygonMode,
    nearDestinations: lereDlpParameters.mainList.nearDestinations,
  })
}

export function fetchDlpYmalRecommendations(destinationId: string) {
  return fetchLocationSeededRecommendations({
    destinationId,
    polygonMode: lereDlpParameters.ymal.polygonMode,
    nearDestinations: lereDlpParameters.ymal.nearDestinations,
    offerTypes: ALL_GRAPH_RECOMMENDATION_OFFER_TYPES,
  })
}

// #endregion

export function fetchPeopleLikeMeRecommendations(userId?: string) {
  return async(dispatch, getState: () => App.State) => {
    const appState = getState()
    const memberId = userId ?? getRecommendationUserId(appState)
    if (!memberId) {
      return
    }
    const personalisedPeopleLikeMeState = appState.recommendations.personalisedPeopleLikeMe
    const region = appState.geo.currentRegionCode

    if (personalisedPeopleLikeMeState.state === 'done') {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME,
      request: async() => await RecommendationService.getPeopleLikeMeRecommendations(memberId, region),
    })
  }
}

export function fetchTopDeals(region: string) {
  return async(dispatch) => {
    dispatch({
      type: API_CALL,
      api: FETCH_LERE_TOP_DEALS,
      request: async() => {
        const { offers, lereVersion } = await RecommendationService.getTopDeals(region)

        const leOfferIds = offers.filter(o => [OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE]
          .includes(o.offer_type))
          .map(o => o.offer_id)

        return { offerIds: leOfferIds, lereVersion }
      },
    })
  }
}

export function fetchHeroPlanner() {
  return async(dispatch, getState: () => App.State) => {
    const heroPlanner = getState().recommendations.heroPlanner

    if (heroPlanner.state === 'done') {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_HERO_PLANNER,
      request: () => RecommendationService.getHeroPlanner(getState().geo.currentRegionCode),
    })
  }
}

export function fetchHighIntentOffers(memberId?: string) {
  return async(dispatch, getState: () => App.State) => {
    if (!config.LERE_HIGH_INTENT_CAROUSEL_ENABLED) { return }

    const state = getState()
    const highIntent = state.recommendations.highIntent
    const userId = memberId ?? getRecommendationUserId(state)

    if (!userId || (highIntent.lereVersion !== 'local' && highIntent.state === 'done')) { return }

    const region = state.geo.currentRegionCode
    dispatch({
      type: API_CALL,
      api: FETCH_LERE_HIGH_INTENT,
      request: () => RecommendationService.getHighIntentOffers(userId, region),
    })
  }
}

export function addAbandonedCart(offerId: string, offerType: App.OfferType, total: number) {
  return async(dispatch, getState: () => App.State) => {
    if (!config.LERE_STICKY_ABANDONED_CARTS_ENABLED) { return }

    const state = getState()
    const userId = getRecommendationUserId(state)

    if (!!userId && state.checkout.cart.items.length > 0) {
      const cart: App.AbandonedCartRecommendation = {
        ...state.checkout.cart,
        isDismissed: false,
        offerId,
        offerType,
        creationTime: timeNowInSecond(),
        category: 'sticky_abandoned_carts',
        total,
      }

      RecommendationService.saveCartInLereServiceWithDebounce(userId, cart)

      dispatch({
        type: LERE_SAVE_ABANDONED_CART,
        cart,
      })
    }
  }
}

export function dismissAbandonedCart(offerId: string) {
  return async(dispatch, getState: () => App.State) => {
    dispatch({
      type: LERE_DISMISS_STICKY_ABANDONED_CART,
      offerId,
    })
    const state = getState()
    const userId = getRecommendationUserId(state)
    const cart = getLatestAbandonedCart(state)
    if (!!userId && !!cart) {
      RecommendationService.saveCartInLereServiceWithDebounce(userId, cart)
    }
  }
}

/**
 * Should be called after checkout
 */
export function removeAbandonedCartsAndDismissOthers(offerIds: string[]) {
  return async(dispatch, getState: () => App.State) => {
    if (!offerIds.length) { return }

    dispatch({
      type: LERE_REMOVE_ABANDONED_CART,
      offerIds,
    })
    dispatch({
      type: LERE_DISMISS_ALL_STICKY_ABANDONED_CART,
    })
    const state = getState()
    const userId = getRecommendationUserId(state)
    if (userId) {
      // this need to be awaited to make sure the AC is removed first
      // before updating the others, else there will be a race condition
      await RecommendationService.removeAbandonedCarts(userId, offerIds)

      const carts = getAllAbandonedCarts(state)
      if (carts.length) {
        RecommendationService.saveAbanonedCarts(userId, carts)
      }
    }
  }
}

export function saveHotLead(hotLead: App.HotLeadOffer) {
  return (dispatch, getState: () => App.State) => {
    // we always save the state locally
    // only call remote save if user logged in
    dispatch({
      type: LERE_SAVE_HOT_LEADS,
      hotLead,
    })

    const state = getState()
    const userId = getRecommendationUserId(state)

    saveHighIntentOffersToLocal([hotLead])

    if (userId) {
      // don't need to await for the response
      RecommendationService.saveHotLeadsWithDebounce(userId, [hotLead])
    }
  }
}

export function dismissHotLead(hotLead: App.HotLeadOffer) {
  return async(dispatch) => {
    const dismissedHotLead = {
      ...hotLead,
      isDismissed: true,
    }
    dispatch(saveHotLead(dismissedHotLead))
  }
}

export function removeHighIntentOffers(offerIds: string[], highIntentCategories: App.HighIntentCategory[]) {
  return async(dispatch, getState) => {
    if (!offerIds.length) { return }

    dispatch({
      type: LERE_REMOVE_HIGH_INTENT,
      offerIds,
      highIntentCategories,
    })

    removeHighIntentOffersFromLocal(offerIds)

    const userId = getCurrentUserId(getState())
    if (userId) {
      // don't need to await for the response

      if (highIntentCategories.includes('recently_viewed')) {
        RecommendationService.removeRecentlyViewedOffers(userId, offerIds)
      }
      // Hot Leads is the subset of Recent Viewed, will remove both
      if (highIntentCategories.includes('sticky_hot_leads')) {
        RecommendationService.removeHotLeads(userId, offerIds)
        RecommendationService.removeRecentlyViewedOffers(userId, offerIds)
      }
      // Abandoned cart is the subset of Hot Leads and Recent Viewed, will remove all
      if (highIntentCategories.includes('sticky_abandoned_carts')) {
        RecommendationService.removeAbandonedCarts(userId, offerIds)
        RecommendationService.removeHotLeads(userId, offerIds)
        RecommendationService.removeRecentlyViewedOffers(userId, offerIds)
      }
    }
  }
}

export function saveAllHotLeadsAndRecentlyViewed() {
  return async(_, getState) => {
    const state = getState()
    const userId = getCurrentUserId(state)
    if (!userId) { return }

    const hotLeads = getAllHotLeadOffers(state)
    if (hotLeads.length) {
      RecommendationService.saveHotLeadsWithDebounce(userId, hotLeads)
    }
    const recentlyViewed = getRecentlyViewedOffers(state)
    if (recentlyViewed.length) {
      RecommendationService.saveRecentlyViewedOffersWithDebounce(userId, recentlyViewed, state.geo.currentRegionCode)
    }
  }
}

export function fetchPersonalisedAlternativeOffers(memberId?: string) {
  return async(dispatch, getState: () => App.State) => {
    const state = getState()
    const alternatives = state.recommendations.personalisedAlternatives
    const userId = memberId ?? getRecommendationUserId(state)

    if (!userId || alternatives.state === 'done') { return }

    const region = state.geo.currentRegionCode
    dispatch({
      type: API_CALL,
      api: FETCH_LERE_PERSONALISED_ALTERNATIVES,
      request: () => RecommendationService.enquirePersonalisedAlternativeRecommendations(userId, region),
    })
  }
}

export function fetchPersonalisedAlternativeOffersWithOfferEnquires(offerEnquiries?: RecommendationService.AlternativeOfferEnquiry[]) {
  return async(dispatch, getState: () => App.State) => {
    const state = getState()
    const alternatives = state.recommendations.personalisedAlternativesWithOfferEnquiries
    const finalOfferEnquiries = offerEnquiries || getAlternativeOfferEnquiry(state)
    const alternativeByOffersKey = constructAlternativeOffersKey(finalOfferEnquiries)

    if (finalOfferEnquiries.length === 0 || alternatives[alternativeByOffersKey]?.state === 'done') { return }
    // As we want to support not-login users, we created a user as default for LERE only
    const userId = '67b2f25b-5770-4bda-947e-3153af74232a'
    const region = state.geo.currentRegionCode
    dispatch({
      type: API_CALL,
      api: FETCH_LERE_PERSONALISED_ALTERNATIVES_WITH_OFFER_ENQUIRES,
      key: alternativeByOffersKey,
      request: () => RecommendationService.enquirePersonalisedAlternativeRecommendations(userId, region, offerEnquiries),
    })
  }
}

export function fetchOfferStats(offerId: string, region: string) {
  return async(dispatch, getState: () => App.State) => {
    const state = getState()
    const offerKey = getOfferStatsKey(offerId, region)
    const offerStats = state.recommendations.offerStats[offerKey]

    // skip API call if already fetching or fetched
    if (!!offerStats && (offerStats.state === 'done' || offerStats.state === 'loading')) { return }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_OFFER_STATS,
      offerKey,
      request: () => RecommendationService.getOfferStats(offerId, region),
    })
  }
}

export function fetchLocalHighIntentOffers() {
  return async(dispatch, getState) => {
    const state = getState()
    const userId = getCurrentUserId(state)
    if (userId) { return }

    dispatch({
      type: GET_LOCAL_HIGH_INTENT_OFFERS,
      offers: getHighIntentOffersFromLocal(),
    })
  }
}

export function fetchUserPreferences() {
  return async(dispatch, getState) => {
    const state = getState()
    const userId = getCurrentUserId(state)
    if (!userId) { return }

    dispatch({
      type: API_CALL,
      api: FETCH_USER_PREFERENCES,
      request: () => RecommendationService.getUserPreferences(userId),
    })
  }
}

export function saveUserPreferences(preferences: App.UserPreferences) {
  return async(dispatch, getState) => {
    const state = getState()
    const userId = getCurrentUserId(state)

    if (!userId) { return }
    const isUpdate = !!state.recommendations.userPreferences.preferences
    const request = isUpdate ? RecommendationService.updateUserPreferences : RecommendationService.saveUserPreferences

    dispatch({
      type: API_CALL,
      api: SUBMIT_USER_PREFERENCES,
      request: () => request(userId, preferences),
      preferences,
    })
  }
}

export function fetchLereTrendingDestinations(placeId: string) {
  return async(dispatch, _) => {
    dispatch({
      type: API_CALL,
      api: FETCH_LERE_TRENDING_DESTINATIONS,
      request: () => RecommendationService.getRecommendedTrendingDestinations(placeId),
    })
  }
}

export function dismissLereUserPreferencesNotification() {
  return (dispatch) => {
    dismissUserPreferencesNotificationLocally()
    dispatch({
      type: LERE_DISMISS_USER_PREFERENCES_NOTIFICATION,
    })
  }
}

export function getLereUserPreferencesNotificationDismissed() {
  return (dispatch) => {
    dispatch({
      hasDismissed: getHasDismissedUserPreferencesNotificationLocally(),
      type: LERE_HAS_DISMISSED_USER_PREFERENCES_NOTIFICATION,
    })
  }
}

export function fetchPersonalisedEnsembleModel(userId: string, region: string) {
  return async(dispatch, getState: () => App.State) => {
    const state = getState()
    const ensembleRecommendations = state.recommendations.ensembleModel

    if (ensembleRecommendations.state === 'done') { return }
    dispatch({
      type: API_CALL,
      api: FETCH_LERE_PERSONALISED_ENSEMBLE_MODEL,
      request: () => RecommendationService.getRecommendedEnsembleModel(userId, region),
    })
  }
}

const DEFAULT_RECOMMENDATION_KEY = 'lere_default'
const DEFAULT_RECOMMENDATION_TTL = 60 * 60 // 1 hr

export function saveDefaultRecommendation(source: string, offerIds: string[]) {
  return (dispatch) => {
    if (!offerIds.length) { return }

    const defaultRecommendation: App.DefaultRecommendation = { offerIds, source }
    localStorageSet(DEFAULT_RECOMMENDATION_KEY, defaultRecommendation, DEFAULT_RECOMMENDATION_TTL)

    dispatch({
      defaultRecommendation,
      type: LERE_SAVE_DEFAULT_RECOMMENDATION,
    })
  }
}

export function loadDefaultRecommendation() {
  return (dispatch, getState) => {
    const state: App.State = getState()
    const existing = state.recommendations.defaultRecommendation.offerIds

    if (existing.length) { return }

    const defaultRecommendation = localStorageGet<App.DefaultRecommendation>(DEFAULT_RECOMMENDATION_KEY)

    if (defaultRecommendation?.offerIds?.length) {
      dispatch({
        defaultRecommendation,
        type: LERE_SAVE_DEFAULT_RECOMMENDATION,
      })
    }
  }
}
